import { useState } from 'react';
import { Card, Button, Input } from '../../Common/DesignComponents.jsx';
import AnsweringUI from './AnsweringUI.jsx';
import Waveform from '../../Common/Waveform.jsx';

export function SoundOffPlayer({ room = {}, playerId, onSendAction, audioEngine }) {
  const [authoredPromptText, setAuthoredPromptText] = useState('');
  const [hasSubmittedAuthor, setHasSubmittedAuthor] = useState(false);
  const [hasSubmittedAnswer, setHasSubmittedAnswer] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [votedChoice, setVotedChoice] = useState(null);

  const currentPhase = room?.currentPhase || room?.phase || 'authoring';
  const modeState = room?.modeState || {};
  const assignedPrompt = modeState?.assignedPrompts?.[playerId] || { id: 'prm_demo', text: 'Something super funny happened...' };

  // Rule 3: NO hardcoded/shared clip fallbacks here. Uses player's dealt hand strictly.
  const hand = modeState?.playerHands?.[playerId] || [];

  const duels = modeState?.duels || [];
  const currentDuelIndex = modeState?.currentDuelIndex || 0;
  const currentDuel = duels[currentDuelIndex];

  const isDuelist = currentDuel && (playerId === currentDuel.duelistAId || playerId === currentDuel.duelistBId);
  const isVotingStage = modeState?.playbackStage === 'VOTING' || currentDuel?.stage === 'voting' || currentPhase === 'voting';

  const handleAuthorSubmit = (e) => {
    e.preventDefault();
    if (!authoredPromptText.trim()) return;
    setHasSubmittedAuthor(true);
    if (onSendAction) {
      onSendAction({
        actionType: 'author_prompt',
        prompt: authoredPromptText.trim()
      });
    }
  };

  const handleAnswerSubmit = ({ clipIds, clips }) => {
    setHasSubmittedAnswer(true);
    if (onSendAction) {
      onSendAction({
        actionType: 'submit_answer',
        clipIds,
        clips
      });
    }
  };

  const handleSkipPrompt = () => {
    if (onSendAction) {
      onSendAction({ actionType: 'skip_prompt' });
    }
  };

  const handleVoteSubmit = (choice) => {
    setHasVoted(true);
    setVotedChoice(choice);
    if (onSendAction) {
      onSendAction({
        actionType: 'submit_vote',
        vote: choice
      });
    }
  };

  const handleAuditionClip = (clip) => {
    if (audioEngine && audioEngine.playSequence) {
      audioEngine.playSequence([{ clipId: clip.id, gain: 0.3 }], { startAt: 0 });
    }
  };

  // Heights for equalizer bars matching Listen.dc.html
  const meterHeights = [38, 66, 92, 118, 128, 112, 84, 58, 34];

  return (
    <div
      className="soundoff-player-container"
      data-testid="soundoff-player"
      style={{ width: '100%', boxSizing: 'border-box' }}
    >
      <style>{`
        @keyframes sd-breathe { 0%,100% { opacity: 0.45; } 50% { opacity: 1; } }
      `}</style>

      {/* Phase 1: Prompt Authoring */}
      {currentPhase === 'authoring' && (
        <Card variant="purple" data-testid="player-authoring-view" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '1.4rem', color: '#f8fafc', marginBottom: '8px', fontWeight: 800 }}>
            ✍️ WRITE A PROMPT
          </h3>
          <p style={{ fontSize: '0.9rem', color: '#c7d2fe', marginBottom: '20px' }}>
            Write a scenario or phrase for someone else to answer with audio clips!
          </p>

          {hasSubmittedAuthor ? (
            <div
              data-testid="author-submitted-msg"
              style={{
                background: 'rgba(34, 197, 94, 0.15)',
                border: '1px solid #22c55e',
                color: '#4ade80',
                padding: '16px',
                borderRadius: '12px',
                textAlign: 'center',
                fontWeight: 700
              }}
            >
              ✓ Prompt Submitted! Look at the TV screen while others finish...
            </div>
          ) : (
            <form onSubmit={handleAuthorSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Input
                data-testid="author-prompt-input"
                placeholder="e.g. When the microwave finishes at 3 AM..."
                value={authoredPromptText}
                onChange={(e) => setAuthoredPromptText(e.target.value)}
                maxLength={120}
                required
                style={{ minHeight: '44px' }}
              />
              <Button
                variant="primary"
                size="lg"
                type="submit"
                data-testid="author-prompt-submit-btn"
                disabled={!authoredPromptText.trim()}
                style={{ minHeight: '44px', minWidth: '44px' }}
              >
                Submit Prompt
              </Button>
            </form>
          )}
        </Card>
      )}

      {/* Phase 2: Answering Phase */}
      {currentPhase === 'answering' && (
        <AnsweringUI
          assignedPrompt={assignedPrompt}
          hand={hand}
          onAudition={handleAuditionClip}
          onSubmitAnswer={handleAnswerSubmit}
          onSkipPrompt={handleSkipPrompt}
          isSubmitted={hasSubmittedAnswer}
        />
      )}

      {/* Phase 3: Duel & Reveal Phase */}
      {(currentPhase === 'duel' || currentPhase === 'reveal') && (
        <Card
          variant="dark"
          data-testid="player-duel-view"
          style={{
            padding: '20px',
            background: '#08090B',
            border: '1px solid #1C2229',
            borderRadius: '16px'
          }}
        >
          {/* If duelist or playback is active (not voting stage yet) -> Render Listening View matching Listen.dc.html */}
          {isDuelist || !isVotingStage ? (
            <div
              data-testid="player-duelist-banner"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '24px',
                padding: '20px 10px',
                textAlign: 'center'
              }}
            >
              {/* Equalizer Level Meter matching Listen.dc.html */}
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '5px', height: '128px' }}>
                {meterHeights.map((h, i) => (
                  <div
                    key={i}
                    style={{
                      width: '7px',
                      height: `${h}px`,
                      borderRadius: '4px',
                      background: i === 4 ? '#3ACFCF' : (i === 3 || i === 5 ? 'rgba(58,207,207,0.62)' : 'rgba(58,207,207,0.30)'),
                      animation: `sd-breathe ${(1.1 + (i % 3) * 0.35).toFixed(2)}s ease-in-out infinite`,
                      animationDelay: `${(i * 0.09).toFixed(2)}s`
                    }}
                  />
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '25px', fontWeight: 800, letterSpacing: '-0.024em', color: '#EDEFF2' }}>
                  Listen up.
                </div>
                <div style={{ fontSize: '14.5px', lineHeight: 1.5, color: '#79838D', maxWidth: '28ch' }}>
                  {isDuelist
                    ? 'Your pair is playing on the big screen. Nothing to do on your phone.'
                    : `${currentDuel?.duelistA?.name || 'Duelist'}'s pair is playing on the big screen. Nothing to do on your phone.`}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', borderRadius: '999px', border: '1px solid #1F252C' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3ACFCF' }} />
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#69737C' }}>
                  Duel {currentDuelIndex + 1} of {duels.length || 1}
                </span>
              </div>
            </div>
          ) : (
            /* Voting View matching Listen.dc.html */
            <div data-testid="player-voting-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6E7982', marginBottom: '8px' }}>
                  The prompt was
                </div>
                <div style={{ fontSize: '18px', lineHeight: 1.3, fontWeight: 700, color: '#EDEFF2' }}>
                  "{currentDuel?.prompt?.text || 'Sound Off Duel'}"
                </div>
              </div>

              {hasVoted ? (
                <div
                  data-testid="vote-submitted-msg"
                  style={{
                    background: 'rgba(34, 197, 94, 0.15)',
                    border: '1px solid #22c55e',
                    color: '#4ade80',
                    padding: '16px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    fontWeight: 700
                  }}
                >
                  ✓ Vote Submitted ({votedChoice === 'A' ? (currentDuel?.duelistA?.name || 'Choice A') : (currentDuel?.duelistB?.name || 'Choice B')})!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Voting Card A */}
                  <div
                    data-testid="player-vote-btn-a"
                    onClick={() => handleVoteSubmit('A')}
                    style={{
                      minHeight: '96px',
                      borderRadius: '14px',
                      border: votedChoice === 'A' ? '1px solid #E4738A' : '1px solid #1C2229',
                      background: votedChoice === 'A' ? 'rgba(255,255,255,0.035)' : '#0E1116',
                      padding: '14px 15px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: '11px',
                      cursor: 'pointer',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: votedChoice === 'A' ? '#EDEFF2' : '#AEB7BF' }}>
                        {currentDuel?.duelistA?.name || 'Player A'}
                      </span>
                      {votedChoice === 'A' && (
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600, color: '#E4738A' }}>
                          Your vote
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <Waveform seed="duelistA_1" bars={24} height={24} color="#E4738A" dimColor="#333B43" showPlayhead={false} />
                      <div style={{ width: '1px', height: '22px', background: '#262D34', flex: 'none' }} />
                      <Waveform seed="duelistA_2" bars={24} height={24} color="#E4738A" dimColor="#333B43" showPlayhead={false} />
                    </div>
                  </div>

                  {/* Voting Card B */}
                  <div
                    data-testid="player-vote-btn-b"
                    onClick={() => handleVoteSubmit('B')}
                    style={{
                      minHeight: '96px',
                      borderRadius: '14px',
                      border: votedChoice === 'B' ? '1px solid #E0A458' : '1px solid #1C2229',
                      background: votedChoice === 'B' ? 'rgba(255,255,255,0.035)' : '#0E1116',
                      padding: '14px 15px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: '11px',
                      cursor: 'pointer',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: votedChoice === 'B' ? '#EDEFF2' : '#AEB7BF' }}>
                        {currentDuel?.duelistB?.name || 'Player B'}
                      </span>
                      {votedChoice === 'B' && (
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600, color: '#E0A458' }}>
                          Your vote
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <Waveform seed="duelistB_1" bars={24} height={24} color="#E0A458" dimColor="#333B43" showPlayhead={false} />
                      <div style={{ width: '1px', height: '22px', background: '#262D34', flex: 'none' }} />
                      <Waveform seed="duelistB_2" bars={24} height={24} color="#E0A458" dimColor="#333B43" showPlayhead={false} />
                    </div>
                  </div>
                </div>
              )}

              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px', letterSpacing: '0.08em', color: '#6E7982' }}>
                  {hasVoted ? 'Vote in. You can change it until the timer ends.' : 'One vote. You cannot vote for your own pair.'}
                </span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Phase 4: Results */}
      {(currentPhase === 'results' || currentPhase === 'game_over') && (
        <Card variant="purple" data-testid="player-results-view" style={{ padding: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '8px' }}>🏁</div>
          <h2 style={{ fontSize: '1.6rem', color: '#f8fafc', margin: '0 0 8px 0', fontWeight: 900 }}>
            ROUND COMPLETE
          </h2>
          <p style={{ color: '#c7d2fe', fontSize: '1rem' }}>
            Check the TV screen for final duel scores and author bonuses!
          </p>
        </Card>
      )}
    </div>
  );
}

export default SoundOffPlayer;
